import React, { useLayoutEffect, useState, useRef, useEffect } from 'react';
import { Dimensions, Image, ScrollView, StyleSheet, Text, View, Platform } from 'react-native';
import { WebView } from 'react-native-webview';
import Button from '../../Button';
import { Color, CommonStyles, CommonProperties } from '../../../styles';
import { useNavigation } from '@react-navigation/native';
import { rightDeleteReceiptOption } from '../../../utils/defaultNavOptions';
import { useDispatch } from 'react-redux';
import { openAlert } from '../../../store/actions/alert';
import { useTranslation } from 'react-i18next';
import { i18nNS, useComponentTKey } from '../../../i18n';
import { deleteReceiptFromUser } from '../../../services/api/expense.service';
import LoaderOverlay from '../LoaderOverlay';
import { isNullOrEmpty } from '../../../utils/common.utils';
import { Zoomable } from '@likashefqet/react-native-image-zoom';
import { scale, scaleVertically } from '../../../utils/scale.utils';
import { PDF_MIME_TYPE } from '../../../utils/image.utils';
import { isIPhoneBrowser } from '../../../utils/expense.utils';

const { height: screenHeight } = Dimensions.get('window');
const MAX_RETRY_COUNT = 5;

const ViewReceiptModal = ({ route }) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const { params } = route || {};
  const {
    receipt = {},
    removeSelectedImageFromState,
    showSelectReceipt = true,
    toggleSelectImage,
    isEditable = true,
  } = params;
  const { value, createDate, receiptId, file = null } = receipt;
  const { t } = useTranslation(i18nNS.tripExpense);
  const { tKey } = useComponentTKey('ExpenseScreen');
  const [isDeleting, setIsDeleting] = useState(false);
  const isPdf = (file && file.includes(PDF_MIME_TYPE)) || (value && value.includes('.pdf'));
  const [isLoading, setIsLoading] = useState(isPdf);
  const [retryCount, setRetryCount] = useState(0);
  const onLoadStartTriggered = useRef(false); // Track if onLoadStart was triggered
  const [pdfFailedToLoadOnWeb, setPdfFailedToLoadOnWeb] = useState(false);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitleAlign: 'center',
      ...(isEditable && rightDeleteReceiptOption(() => handleDelete())),
    });
  }, []);

  // if the goggle pdf viewer is not loaded after 2 seconds then show standard iframe
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    // Set a timeout to check if the PDF is still loading after 2 seconds
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        return;
      }
      setPdfFailedToLoadOnWeb(true);
    }, 2000); // 2 seconds
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading]);

  const handleDelete = () => {
    dispatch(
      openAlert({
        title: t(tKey('ConfirmDeletionTitle')),
        message: t(tKey('VerifyDeletion')),
        confirmText: t(tKey('ConfirmDeletionButton')),
        cancelText: t('common:Cancel'),
        showProgress: true,
        onConfirmPressed: async () => {
          setIsDeleting(true);
          await deleteReceiptFromUser(receipt);
          removeSelectedImageFromState(receiptId);
          navigation.goBack();
        },
      })
    );
  };

  const handlePress = async () => {
    toggleSelectImage(receipt);
    navigation.goBack();
  };

  const googleDocsViewerUrl = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
    value
  )}`;

  const handleRetry = () => {
    if (retryCount < MAX_RETRY_COUNT) {
      console.log('Retrying to load PDF...');
      setRetryCount(retryCount + 1);
      onLoadStartTriggered.current = false; // Reset the flag
    } else {
      alert('Failed to load PDF after multiple attempts. Please try again later.');
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView>
        {isDeleting && <LoaderOverlay loadingText={t(tKey('Deleting'))} />}
        <View style={styles.headerContainer}>
          <View style={styles.headerTextWrapper}>
            <Text style={styles.dateText}>
              {'Uploaded on '} {createDate}
            </Text>
          </View>
        </View>
        {isPdf ? (
          Platform.OS === 'web' ? (
            <iframe
              src={pdfFailedToLoadOnWeb ? value : googleDocsViewerUrl}
              onLoad={() => setIsLoading(false)}
              style={styles.webPdf}
              title="PDF Viewer"
            />
          ) : (
            <WebView
              key={`${googleDocsViewerUrl}-${retryCount}`} // Force re-render on retry
              source={{
                uri: googleDocsViewerUrl,
              }}
              style={styles.pdf}
              onLoadStart={() => {
                onLoadStartTriggered.current = true; // Mark that onLoadStart was triggered
                setIsLoading(true);
                console.log('WebView load start');
              }}
              onLoadEnd={() => {
                console.log('WebView load end');
                // Check if onLoadStart was triggered
                if (onLoadStartTriggered.current) {
                  setIsLoading(false);
                } else {
                  console.warn('onLoadStart was not triggered. Retrying...');
                  handleRetry();
                }
              }}
            />
          )
        ) : isIPhoneBrowser() ? (
          <Image style={styles.image} source={{ uri: isNullOrEmpty(file) ? value : file }} />
        ) : (
          <Zoomable isDoubleTapEnabled>
            <Image style={styles.image} source={{ uri: isNullOrEmpty(file) ? value : file }} />
          </Zoomable>
        )}
        {isLoading && <LoaderOverlay loadingText="Loading PDF..." />}
      </ScrollView>
      {showSelectReceipt && (
        <Button
          style={styles.selectButton}
          onPress={handlePress}
          label="Select Receipt"
          labelStyle={CommonStyles.h2}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    alignItems: 'center',
  },
  headerTextWrapper: {
    backgroundColor: Color.accent,
    borderRadius: 25,
    paddingHorizontal: 20,
    paddingVertical: 5,
    marginTop: 10,
  },
  image: {
    flex: 1,
    width: '100%',
    height: screenHeight - scaleVertically(60),
    resizeMode: 'contain',
  },
  pdf: {
    flex: 1,
    width: '100%',
    height: screenHeight - scaleVertically(60),
  },
  webPdf: {
    width: '100%',
    height: screenHeight - scaleVertically(60),
    border: 'none',
  },
  dateText: {
    fontSize: 16,
    color: Color.primary,
    textAlign: 'center',
  },
  selectButton: {
    position: 'absolute',
    bottom: scale(20),
    left: scale(20),
    right: scale(20),
    ...CommonProperties.py3,
    ...CommonProperties.borderRadius4,
  },
});

export default ViewReceiptModal;
